import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  ChevronRight,
  ExternalLink,
  FileText,
  Loader2,
  MessageSquare,
  PlayCircle,
  Printer,
  Search,
  Wrench,
} from 'lucide-react';

import { useBubbly } from '@/components/bubbly/BubblyContext';
import { MrBubblesFull } from '@/components/tour/MrBubbles';
import { useKbArticles, type KbArticleSummary } from '@/hooks/useKbArticles';
import { cn } from '@/lib/utils';

/**
 * /resources — the customer help library. Renders every customer-visible
 * knowledge-base document (the same library Mr. Bubbles answers from, managed
 * on /admin/knowledge) as a browsable, searchable set of guides, plus the
 * how-to videos. "Ask Mr. Bubbles" opens the assistant in its big window.
 */

const VIDEOS = [
  {
    title: 'Connecting a new Bag-in-Box',
    description: 'Step-by-step: hooking up a fresh syrup box the right way.',
    url: 'https://adilo.bigcommand.com/watch/DAWMMwPL',
  },
  {
    title: 'Disconnecting a Bag-in-Box',
    description: 'Removing the syrup line from an empty box.',
    url: 'https://adilo.bigcommand.com/watch/QNELQiDS',
  },
];

/**
 * Curated grouping for known guides. kb_documents.category defaults to the
 * slug, so grouping lives here; anything unknown lands in "More guides"
 * (admin-added docs can also set a category that matches a group name).
 */
/** The signed account terms — always the first, most prominent group. */
const TERMS_SLUG = 'account-terms-and-conditions';

const GROUPS: Array<{ name: string; slugs: string[] }> = [
  {
    name: 'Account terms & policies',
    slugs: [TERMS_SLUG],
  },
  {
    name: 'Troubleshooting',
    slugs: [
      'soda-machine-troubleshooting',
      'soda-machine-dispensing-failures',
      'fountain-dispenser-troubleshooting',
      'bar-gun-troubleshooting',
      'ice-machine-troubleshooting',
    ],
  },
  {
    name: 'CO₂, syrup & supplies',
    slugs: ['co2-troubleshooting-and-safety', 'tank-co2-rentals', 'bib-syrup-handling'],
  },
  {
    name: 'Cleaning & maintenance',
    slugs: [
      'preventive-maintenance-cleaning',
      'beverage-quality-standard',
      'fountain-startup-shutdown',
    ],
  },
  {
    name: 'Frozen, juice & brewed drinks',
    slugs: ['frozen-drink-machine-guide', 'juice-dispenser-guide', 'tea-coffee-brewer-guide'],
  },
  {
    name: 'Ordering, billing & the portal',
    slugs: ['portal-user-guide', 'ordering-delivery-faqs', 'account-billing-faqs'],
  },
];

const GROUP_BY_SLUG = new Map(GROUPS.flatMap((g) => g.slugs.map((s) => [s, g.name] as const)));
// The library index doc duplicates what this page IS — skip it in the list.
const HIDDEN_SLUGS = new Set(['alameda-soda-resource-library']);

function groupArticles(articles: KbArticleSummary[]) {
  const byGroup = new Map<string, KbArticleSummary[]>();
  for (const a of articles) {
    if (HIDDEN_SLUGS.has(a.slug)) continue;
    const groupFromCategory = GROUPS.find(
      (g) => g.name.toLowerCase() === (a.category ?? '').trim().toLowerCase(),
    )?.name;
    const group = GROUP_BY_SLUG.get(a.slug) ?? groupFromCategory ?? 'More guides';
    const list = byGroup.get(group) ?? [];
    list.push(a);
    byGroup.set(group, list);
  }
  const ordered: Array<{ name: string; articles: KbArticleSummary[] }> = [];
  for (const g of GROUPS) {
    const list = byGroup.get(g.name);
    if (list?.length) {
      // Keep the curated order for known slugs, alphabetical for the rest.
      list.sort((a, b) => {
        const ia = g.slugs.indexOf(a.slug);
        const ib = g.slugs.indexOf(b.slug);
        if (ia !== -1 && ib !== -1) return ia - ib;
        if (ia !== -1) return -1;
        if (ib !== -1) return 1;
        return (a.title ?? a.slug).localeCompare(b.title ?? b.slug);
      });
      ordered.push({ name: g.name, articles: list });
    }
  }
  const more = byGroup.get('More guides');
  if (more?.length) ordered.push({ name: 'More guides', articles: more });
  return ordered;
}

export default function Resources() {
  const { data: articles, isLoading, error } = useKbArticles();
  const { openBubbly } = useBubbly();
  const [query, setQuery] = useState('');

  const groups = useMemo(() => {
    if (!articles) return [];
    const q = query.trim().toLowerCase();
    const filtered = q
      ? articles.filter((a) => (a.title ?? a.slug).toLowerCase().includes(q))
      : articles;
    return groupArticles(filtered);
  }, [articles, query]);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* Hero */}
      <div className="flex flex-col gap-4 rounded-2xl border border-brix-border bg-brix-primary-tint/30 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <MrBubblesFull size={88} className="hidden shrink-0 sm:block" />
          <div>
            <h1 className="text-xl font-bold text-brix-ink">Resources & how-to guides</h1>
            <p className="mt-1 max-w-xl text-sm text-brix-muted">
              Troubleshooting, cleaning procedures, CO₂ safety, and videos for your fountain, bar
              gun, and ice equipment — or just ask Mr. Bubbles and he'll walk you through it.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => openBubbly({ expanded: true })}
          className={cn(
            'inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-brix-primary px-4 py-3',
            'text-sm font-semibold text-white shadow-card transition-all hover:scale-[1.02] active:scale-95',
          )}
        >
          <MessageSquare size={16} /> Ask Mr. Bubbles
        </button>
      </div>

      {/* Account terms — always visible, not just buried in the groups below */}
      <Link
        to={`/resources/${TERMS_SLUG}`}
        className={cn(
          'flex items-center gap-3 rounded-xl border border-brix-border bg-brix-surface p-4',
          'transition-all hover:border-brix-primary/40 hover:shadow-card',
        )}
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brix-primary-tint text-brix-primary">
          <FileText size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-brix-ink">Account Terms & Conditions</p>
          <p className="mt-0.5 text-xs text-brix-muted">
            The terms you agreed to at signup — payment, deliveries, and equipment &amp; CO₂
            rental terms. Printable, in case you need to share it.
          </p>
        </div>
        <span className="flex shrink-0 items-center gap-1 text-xs font-semibold text-brix-primary">
          <Printer size={13} /> View &amp; print
        </span>
      </Link>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-brix-muted" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search guides — e.g. flat soda, foaming, ice machine, CO₂…"
          className={cn(
            'w-full rounded-xl border border-brix-border bg-brix-surface py-2.5 pl-9 pr-3 text-sm',
            'placeholder:text-brix-muted focus-visible:border-brix-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brix-primary/30',
          )}
        />
      </div>

      {isLoading && (
        <p className="py-10 text-center text-sm text-brix-muted">
          <Loader2 size={15} className="mr-1 inline animate-spin" /> Loading the library…
        </p>
      )}
      {error && (
        <p className="py-10 text-center text-sm text-brix-muted">
          The library didn't load — try again, or ask Mr. Bubbles instead.
        </p>
      )}

      {/* Article groups */}
      {groups.map((group) => (
        <section key={group.name}>
          <h2 className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-tight text-brix-muted">
            {group.name === 'Troubleshooting' ? <Wrench size={14} /> : <BookOpen size={14} />}
            {group.name}
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {group.articles.map((a) => (
              <Link
                key={a.slug}
                to={`/resources/${a.slug}`}
                className={cn(
                  'group flex items-start justify-between gap-2 rounded-xl border border-brix-border bg-brix-surface p-4',
                  'transition-all hover:border-brix-primary/40 hover:shadow-card',
                )}
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold leading-snug text-brix-ink">
                    {a.title ?? a.slug}
                  </p>
                  {a.updated_at && (
                    <p className="mt-1 text-[11px] text-brix-muted">
                      Updated {new Date(a.updated_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <ChevronRight
                  size={16}
                  className="mt-0.5 shrink-0 text-brix-muted transition-transform group-hover:translate-x-0.5 group-hover:text-brix-primary"
                />
              </Link>
            ))}
          </div>
        </section>
      ))}

      {/* Videos */}
      {!query && (
        <section>
          <h2 className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-tight text-brix-muted">
            <PlayCircle size={14} /> How-to videos
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {VIDEOS.map((v) => (
              <div
                key={v.url}
                className="overflow-hidden rounded-xl border border-brix-border bg-brix-surface"
              >
                <div className="aspect-video w-full bg-black/5">
                  <iframe
                    src={v.url}
                    title={v.title}
                    className="h-full w-full"
                    allow="fullscreen"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
                <div className="flex items-start justify-between gap-2 p-3">
                  <div>
                    <p className="text-sm font-semibold text-brix-ink">{v.title}</p>
                    <p className="mt-0.5 text-xs text-brix-muted">{v.description}</p>
                  </div>
                  <a
                    href={v.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-0.5 shrink-0 text-brix-muted hover:text-brix-primary"
                    aria-label={`Open ${v.title} in a new tab`}
                  >
                    <ExternalLink size={15} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Footer CTA */}
      <div className="rounded-xl border border-brix-border bg-brix-surface p-4 text-center text-sm text-brix-muted">
        Can't find it? Ask{' '}
        <button
          type="button"
          onClick={() => openBubbly({ expanded: true })}
          className="font-semibold text-brix-primary hover:underline"
        >
          Mr. Bubbles
        </button>{' '}
        — he can walk you through any of these guides and file a service call if you need a
        technician.
      </div>
    </div>
  );
}
